import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, PushPin, PushPinSlash, Trash, MagnifyingGlass, PencilSimple,
  CaretDown, CaretUp, ChatCircleDots,
} from '@phosphor-icons/react';
import { jstDateTime } from '@/utils/jstDate';

const API = process.env.REACT_APP_BACKEND_URL;

const dayBucket = (iso) => {
  const d = new Date(iso);
  const now = new Date();
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  const diff = Math.floor((startOf(now) - startOf(d)) / 86400000);
  if (diff <= 0) return '今日';
  if (diff === 1) return '昨日';
  if (diff <= 7) return '過去7日間';
  if (diff <= 30) return '過去30日間';
  return 'それ以前';
};

function ConvRow({ c, active, onOpen, onPin, onRename, onDelete }) {
  return (
    <div className={`group flex items-center rounded-lg transition-colors ${active
      ? 'bg-violet-100 text-violet-900 ring-1 ring-violet-200'
      : 'text-zinc-600 hover:bg-zinc-100'}`}
      data-testid={`assistant-conv-row-${c.id}`}>
      <button type="button" onClick={() => onOpen(c)}
        title={c.last_preview ? `最新: ${c.last_preview}` : c.title}
        className="flex-1 min-w-0 flex items-center gap-1.5 pl-2.5 pr-1 py-1.5 text-left">
        {c.pinned && <PushPin size={11} weight="fill" className="shrink-0 text-violet-500" />}
        <span className={`truncate text-[12px] ${active ? 'font-semibold' : 'font-medium'}`}>{c.title || '新しい会話'}</span>
        {c.share_token && (!c.share_expires_at || c.share_expires_at > new Date().toISOString()) && (
          <span className="shrink-0 rounded border border-sky-100 bg-sky-50 px-1 py-px text-[8.5px] font-bold text-sky-600"
            title={c.share_expires_at ? `共有中 — ${jstDateTime(c.share_expires_at)} まで有効` : '共有中（無期限）'}
            data-testid={`assistant-conv-shared-${c.id}`}>
            共有中
          </span>
        )}
      </button>
      <span className="flex shrink-0 items-center pr-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button type="button" onClick={() => onPin(c)} data-testid={`assistant-conv-pin-${c.id}`}
          className="p-1 rounded text-zinc-400 hover:text-violet-600 hover:bg-white/70"
          title={c.pinned ? 'ピン留めを解除' : 'ピン留め'}>
          {c.pinned ? <PushPinSlash size={12} /> : <PushPin size={12} />}
        </button>
        <button type="button" onClick={() => onRename(c)} data-testid={`assistant-conv-rename-${c.id}`}
          className="p-1 rounded text-zinc-400 hover:text-zinc-700 hover:bg-white/70" title="名前を変更">
          <PencilSimple size={12} />
        </button>
        <button type="button" onClick={() => onDelete(c)} data-testid={`assistant-conv-delete-${c.id}`}
          className="p-1 rounded text-zinc-400 hover:text-red-600 hover:bg-white/70" title="削除">
          <Trash size={12} />
        </button>
      </span>
    </div>
  );
}

export default function AssistantConvList({ onSelectAssistant }) {
  const [convs, setConvs] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const debRef = useRef(null);

  const load = useCallback(() => {
    axios.get(`${API}/api/assistant/conversations`).then((r) => setConvs(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const onChanged = () => load();
    const onActive = (e) => setActiveId(e.detail?.id || null);
    window.addEventListener('assistant-convs-changed', onChanged);
    window.addEventListener('assistant-active-conv', onActive);
    return () => {
      window.removeEventListener('assistant-convs-changed', onChanged);
      window.removeEventListener('assistant-active-conv', onActive);
    };
  }, [load]);

  useEffect(() => {
    clearTimeout(debRef.current);
    if (!q.trim()) { setResults(null); return; }
    debRef.current = setTimeout(() => {
      axios.get(`${API}/api/assistant/conversations/search`, { params: { q } })
        .then((r) => setResults(r.data || [])).catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(debRef.current);
  }, [q]);

  const open = (c) => {
    onSelectAssistant?.();
    window.dispatchEvent(new CustomEvent('assistant-open-conv', { detail: { id: c.id } }));
    setActiveId(c.id);
  };
  const startNew = () => {
    onSelectAssistant?.();
    window.dispatchEvent(new CustomEvent('assistant-open-conv', { detail: { new: true } }));
  };
  const pin = async (c) => {
    try {
      await axios.patch(`${API}/api/assistant/conversations/${c.id}`, { pinned: !c.pinned });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'ピン留めに失敗しました'); }
  };
  const rename = async (c) => {
    const t = window.prompt('会話のタイトル', c.title || '');
    if (t == null || !t.trim()) return;
    try {
      await axios.patch(`${API}/api/assistant/conversations/${c.id}`, { title: t.trim() });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || '名前の変更に失敗しました'); }
  };
  const del = async (c) => {
    if (!window.confirm('この会話を削除しますか？')) return;
    try {
      await axios.delete(`${API}/api/assistant/conversations/${c.id}`);
      load();
      window.dispatchEvent(new CustomEvent('assistant-conv-deleted', { detail: { id: c.id } }));
      toast.success('会話を削除しました');
    } catch { toast.error('削除に失敗しました'); }
  };

  const pinned = convs.filter((c) => c.pinned);
  const recent = convs.filter((c) => !c.pinned);
  const shown = results ?? null;
  const rowProps = { active: false, onOpen: open, onPin: pin, onRename: rename, onDelete: del };

  let recentBody;
  if (expanded) {
    const groups = [];
    recent.forEach((c) => {
      const b = dayBucket(c.updated_at || c.created_at);
      const g = groups.find((x) => x.label === b);
      if (g) g.items.push(c); else groups.push({ label: b, items: [c] });
    });
    recentBody = groups.map((g) => (
      <div key={g.label}>
        <p className="px-2.5 pt-1.5 pb-0.5 text-[9.5px] font-semibold text-zinc-400">{g.label}</p>
        {g.items.map((c) => <ConvRow key={c.id} c={c} {...rowProps} active={c.id === activeId} />)}
      </div>
    ));
  } else {
    recentBody = recent.slice(0, 4).map((c) => (
      <ConvRow key={c.id} c={c} {...rowProps} active={c.id === activeId} />
    ));
  }

  return (
    <div className="ml-3 mb-1.5 border-l-2 border-violet-100 pl-1.5 space-y-0.5" data-testid="assistant-conv-list">
      <div className="relative px-0.5">
        <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="会話を検索…"
          data-testid="assistant-conv-search"
          className="w-full h-7 rounded-md border border-zinc-200 bg-zinc-50/60 pl-7 pr-2 text-[11px] outline-none focus:border-violet-300 focus:bg-white transition-colors" />
      </div>
      <button type="button" onClick={startNew} data-testid="assistant-new-conv-side"
        className="w-full flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12px] font-medium text-violet-700 hover:bg-violet-50 transition-colors">
        <Plus size={13} weight="bold" /> 新しい会話
      </button>

      {shown !== null ? (
        <>
          <p className="px-2.5 pt-1 text-[9.5px] font-semibold text-zinc-400">検索結果（{shown.length}）</p>
          {shown.length === 0 && <p className="px-2.5 py-1.5 text-[11px] text-zinc-400">該当する会話がありません</p>}
          {shown.map((c) => <ConvRow key={c.id} c={c} {...rowProps} active={c.id === activeId} />)}
        </>
      ) : convs.length === 0 ? (
        <div className="px-2.5 py-2 text-center" data-testid="assistant-conv-empty">
          <ChatCircleDots size={18} className="mx-auto text-zinc-300" />
          <p className="mt-1 text-[11px] text-zinc-400">まだ会話がありません</p>
        </div>
      ) : (
        <>
          {pinned.length > 0 && (
            <>
              <p className="px-2.5 pt-1 text-[9.5px] font-semibold text-zinc-400">ピン留め</p>
              {pinned.map((c) => <ConvRow key={c.id} c={c} {...rowProps} active={c.id === activeId} />)}
            </>
          )}
          <p className="px-2.5 pt-1 text-[9.5px] font-semibold text-zinc-400">最近の会話</p>
          {recentBody}
          {recent.length > 4 && (
            <button type="button" onClick={() => setExpanded((s) => !s)} data-testid="assistant-conv-see-more"
              className="w-full flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] text-zinc-400 hover:text-violet-600 hover:bg-zinc-50 transition-colors">
              {expanded ? <><CaretUp size={11} /> 折りたたむ</> : <><CaretDown size={11} /> さらに表示（{recent.length - 4}件）</>}
            </button>
          )}
        </>
      )}
    </div>
  );
}
